import * as Actions from './Actions';
import * as Auth from './Auth';
import * as Dialog from './Dialog';
import * as Geolocation from './Geolocation';
import * as Keyboard from './Keyboard';
import * as Native from './Native';
import * as Notifications from './Notifications';
import * as Refresh from './Refresh';
import * as Scroll from './Scroll';
import * as Service from '@/App/Service';
import * as Subscription from './Subscription';
import * as Sync from './Sync';
import ChalkySticks from '@chalkysticks/sdk';
import Router from '@/Router';
import Store from '@/Store';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * Whether lifecycle has been initialized
 *
 * @type boolean
 */
let hasInitialized: boolean = false;

/**
 * Whether lifecycle has been booted
 *
 * @type boolean
 */
let hasBooted: boolean = false;

/**
 * Main initialization function that orchestrates all app managers.
 * Call this from App.vue's created() hook.
 *
 * @return Promise<void>
 */
export async function initialize(): Promise<void> {
	if (hasInitialized) {
		ChalkySticks.Utility.Log.warn('app', 'App manager already initialized');
		return;
	}

	ChalkySticks.Utility.Log.info('app', 'Initializing app managers...');

	// Initialize all managers (order matters for some)
	Actions.initialize();
	Auth.initialize();
	Dialog.initialize();
	await Keyboard.initialize();
	await Notifications.initialize();
	Refresh.initialize();
	Scroll.initialize();
	await Subscription.initialize();
	Sync.initialize();

	// Native-only initialization
	await Native.initialize();
	await setupCapacitorApp();

	hasInitialized = true;
	ChalkySticks.Utility.Log.info('app', 'App managers initialized');
}

/**
 * Post-mount boot phase. Handles setup that requires DOM refs and
 * other work that should happen after the Vue component has mounted.
 * Call this from App.vue's mounted() hook.
 *
 * @param any alertPromptRef - Reference to the global alert prompt component
 * @return Promise<void>
 */
export async function boot(alertPromptRef: any): Promise<void> {
	if (hasBooted) {
		ChalkySticks.Utility.Log.warn('app', 'App manager already booted');
		return;
	}

	// Set dialog ref for alert/confirm/prompt dialogs
	Dialog.setAlertPromptRef(alertPromptRef);

	// Initialize version tracking
	Service.VersionHistory.initialize();

	// Setup geolocation (needs ref for permission dialogs)
	Geolocation.setup(alertPromptRef);

	// Record last opened timestamp for splash screen logic
	Store.commit('setLastOpened', Date.now());

	hasBooted = true;
	ChalkySticks.Utility.Log.info('app', 'App boot complete');
}

/**
 * Setup Capacitor App lifecycle listeners.
 * Handles back button, pause, and resume events.
 *
 * @return Promise<void>
 */
async function setupCapacitorApp(): Promise<void> {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	App.addListener('backButton', () => {
		if (Router.currentRoute.name === 'PageHome') {
			App.exitApp();
		} else {
			Router.go(-1);
		}
	});

	App.addListener('pause', () => {
		ChalkySticks.Core.Event.Bus.dispatch('app:pause');
		ChalkySticks.Core.Event.Bus.dispatch('deactivate');
	});

	App.addListener('resume', () => {
		ChalkySticks.Core.Event.Bus.dispatch('app:resume');
		ChalkySticks.Core.Event.Bus.dispatch('activate');
	});

	ChalkySticks.Utility.Log.info('app', 'Capacitor App setup complete');
}
